/**
 * LinkedIn post library, 36 ready-to-publish posts written for Irish &
 * UK artisan food producers. Voice: dry, specific, floor-first, founder-led.
 * Each post: 600-1500 characters (LinkedIn engagement sweet spot).
 * No Silicon Valley vocabulary. No motivational platitudes. No fake stats.
 *
 * Pillar codes match the strategic playbook:
 *   P1 = Excel Truth (operational pain)
 *   P2 = From the Floor (Cashel Blue / customer proof)
 *   P3 = Compliance Decoded (FSAI / BRCGS / EU food safety)
 *   P4 = Tactical Resource (lead magnet / template)
 *   P5 = Founder's POV (Asif on the floor)
 *   P6 = Product Demo (60-sec video / before-after)
 */

export const LINKEDIN_POSTS = [
  // ─── Month 1 ──────────────────────────────────────────────────────
  {
    id: 'w1-mon',
    week: 1,
    day: 'Mon · 08:15 Dublin',
    pillar: 'P1',
    title: 'Spreadsheets are not traceability.',
    format: 'Text · 850 chars',
    hashtags: '#IrishDairy #FoodSafety #Traceability #FSAI #BRCGS',
    body: `Spreadsheets are not traceability.

They're a record. Traceability is a chain, milk intake → silo → make-day → batch → dispatch → customer. Every link timestamped. Every link signed.

Most Irish dairies I walk into have records. Beautiful, colour-coded, well-maintained records.

What they don't have is a chain.

So when FSAI calls at 9am on a Tuesday and asks where Lot 2407-B is, you spend the next four hours stitching seven tabs together while the phone rings on speakerphone.

That's not traceability. That's archaeology.

If you can do a backward trace on a single salt lot in under 90 seconds, you have traceability.

If you can't, you have a record.

Both pass an audit when the auditor is kind. Only one passes when they're not.

Curious where your operation sits? DM me, I'll walk you through three real backward traces, with me on the line. No pitch.`,
  },
  {
    id: 'w1-wed',
    week: 1,
    day: 'Wed · 08:15 Dublin',
    pillar: 'P2',
    title: 'How Cashel Blue replaced 12 months of Excel in 30 days.',
    format: 'Carousel · 6 slides + caption',
    hashtags: '#IrishDairy #FarmhouseCheese #BRCGS #FoodTraceability #Tipperary',
    body: `We didn't migrate Cashel Farmhouse Cheesemakers to Keystone. We rebuilt Keystone around them.

18 months on the floor in Fethard, Co. Tipperary. Watching Marzena (QA) cross-reference 23 tabs of a single workbook every Friday afternoon. Watching Sergio quote a Tesco PO from 2019 off the top of his head because the laptop didn't reach the chiller.

What we found:
→ 23 tabs in the master workbook. Three of them were the same data, copy-pasted weekly.
→ Recall drill: four hours, every time. Always on a Saturday.
→ Dispatch run sheet: paper. Pinned to the cold-room door.
→ Sunday paperwork: a recurring family argument.

What we changed:
→ Recall drill: 4 minutes.
→ Dispatch errors: zero across 90 consecutive days.
→ The workbook still exists. Marzena opens it twice a year now, out of fondness.

Full case study in the comments (with Sarah and Sergio's written permission).

If you're an Irish or UK artisan producer still running off a master workbook, what's the one tab you'd love to delete tomorrow?`,
  },
  {
    id: 'w1-fri',
    week: 1,
    day: 'Fri · 08:15 Dublin',
    pillar: 'P4',
    title: 'FSAI audit prep checklist, free 4-page PDF.',
    format: 'PDF lead magnet · image + link in comments',
    hashtags: '#FSAI #FoodSafety #IrishFood #AuditReady #BRCGS',
    body: `Made you a thing.

A 4-page FSAI audit prep checklist for Irish food producers. 27 specific questions an inspector will actually ask, pulled from anonymised FSAI audit reports across 2024 and 2025.

What's on it:
✓ The 7 records they look at first (in order)
✓ The 3 things they ask the QA manager that aren't on any official guidance document
✓ The 5 traceability tests they actually run
✓ What a "minor non-conformance" really means for your next audit window
✓ One page you can print and pin in the office

It's free. No signup. No email gate. Link in the comments.

Built it because three different Irish producers asked me the same questions in the same week. If it saves you one panicked Sunday in the office, that's the whole point.

If you find it useful, share it with the QA manager who is currently on holiday and dreading their return.`,
  },
  {
    id: 'w2-mon',
    week: 2,
    day: 'Mon · 08:15 Dublin',
    pillar: 'P3',
    title: 'BRCGS traceability standards, what changed and what nobody is saying out loud.',
    format: 'Text · long-form · 1300 chars',
    hashtags: '#BRCGS #BRCGSIssue9 #FoodSafety #QualityAssurance #FoodManufacturing',
    body: `BRCGS traceability standards, what changed and what nobody is saying out loud.

The headlines you've read:
→ Annual mock recalls and traceability tests are mandatory now.
→ Both forward and backward.
→ Results documented and reviewed.

What people are not saying:

1. The auditor is allowed to choose the starting point.

Previously, you could run your mock recall on a friendly product, short shelf life, single customer, recently dispatched. BRCGS traceability requirements explicitly lets the auditor pick. That includes the salt lot from 2023 that went into 14 batches across 9 customers.

2. The 4-hour benchmark didn't go away. It got harder.

You still need to demonstrate a full mass-balance reconciliation within 4 hours. BRCGS traceability requirements just added that you need to do it for a randomly-chosen lot, not the one your QA manager rehearsed last Tuesday.

3. "Documented results" means documented to a level that another auditor could replay it.

Sticky-note on a workbook tab does not pass this bar in BRCGS traceability requirements. It passed in Issue 8.

If you've not run a mock recall on a 2-year-old salt lot in the last 6 months, do it before December.

Save this for your next BRCGS prep meeting.`,
  },
  {
    id: 'w2-wed',
    week: 2,
    day: 'Wed · 08:15 Dublin',
    pillar: 'P1',
    title: 'Saturday morning recall. Tuesday morning explanation. Choose one.',
    format: 'Image post · stopwatch graphic',
    hashtags: '#FoodTraceability #Recall #IrishDairy #FoodSafety #BRCGS',
    body: `Saturday morning recall. Tuesday morning explanation.

You only get to choose one.

The dairies still on Excel choose Saturday. Four hours, sometimes five. Phones ringing. Marzena cancelling her plans. Someone hand-writing affected batch lists onto the back of a delivery docket because the printer is in the office and the office is locked.

The dairies on Keystone choose Tuesday. The recall pack is generated in four minutes on a Friday morning. They use the weekend.

Both pass the audit. Only one passes the test of the people involved.

Which one is your operation, today, before lunch?`,
  },
  {
    id: 'w2-fri',
    week: 2,
    day: 'Fri · 08:15 Dublin',
    pillar: 'P6',
    title: '60 seconds, same recall, two systems.',
    format: 'Video · 60 sec screen recording · no audio needed',
    hashtags: '#FoodTraceability #Recall #FoodManufacturing #BRCGS',
    body: `60 seconds. Two screens. Same recall scenario. Real data.

Left: a 23-tab Excel workbook. The QA manager is filtering, copy-pasting, and on her third Ctrl+F.

Right: Keystone. The QA manager picks the starting lot, clicks once. The full downstream chain renders. Affected batches, dispatches, customers, kg. PDF generated. Sent.

Mock recall, end-to-end, in the time it takes to make a coffee.

No sound. No commentary. Watch it and decide what your operation actually looks like.

If you'd like to run the same exercise against your own data (not a demo dataset, yours), reply or DM. We'll do a 20-minute side-by-side. No slides.`,
  },
  {
    id: 'w3-mon',
    week: 3,
    day: 'Mon · 08:15 Dublin',
    pillar: 'P3',
    title: '5 questions every food auditor asks. 4 of them assume you have software.',
    format: 'Carousel · 6 slides',
    hashtags: '#BRCGS #FSAI #FoodSafety #FoodCompliance #AuditReady',
    body: `Five questions a food auditor will ask you this year. Four of them quietly assume you have software.

1. "Show me the full upstream chain for this finished pack, please."
→ Spreadsheet shop: 20 minutes of cross-referencing, fingers crossed on the supplier-lot to milk-intake link.
→ Software shop: one click, one screen.

2. "When was the last successful mock recall?"
→ Spreadsheet shop: a folder somewhere, paper sign-off page.
→ Software shop: timestamped traceability event, status workflow, audit trail.

3. "Has anyone other than the QA manager amended this batch record?"
→ Spreadsheet shop: no way to tell.
→ Software shop: every field has a who/when/why.

4. "Can I pick the starting lot for today's traceability test?"
→ Spreadsheet shop: please don't.
→ Software shop: pick any.

5. "What's your customer-mapping for this internal SKU?"
→ Spreadsheet shop: a tab. Possibly two tabs.
→ Software shop: one master record, every customer template auto-generated.

The 5th question is the only one a spreadsheet shop can answer well. Auditors know this. So they ask the other four first.

DM "AUDIT" if you'd like the full version as a PDF.`,
  },
  {
    id: 'w3-wed',
    week: 3,
    day: 'Wed · 08:15 Dublin',
    pillar: 'P2',
    title: 'What 40+ years of farmhouse production taught us about software.',
    format: 'Text + photo · long-form',
    hashtags: '#IrishDairy #FarmhouseCheese #FoodManufacturing #Tipperary #Software',
    body: `What 40+ years of farmhouse production taught us about software.

In November 2024 I walked into the make-room at Cashel Farmhouse Cheesemakers for the first time, with a notebook and a pen and no software.

By February 2026 we'd shipped Keystone.

The single biggest thing I learned in between:

Production floors don't need software that does more. They need software that demands less.

Software that doesn't ask you to retype the dispatch address. That knows Tesco Distribution Tipperary wants the pallet label on the short side and Lidl Charleville wants it on the long side. That doesn't ask "are you sure?" when you're holding 18kg of cheese curd and wearing a hairnet.

Software that knows the difference between a make-day, a batch, an intake, and a silo, and never asks you to explain.

Software that prints the dispatch note before the lorry has finished reversing.

Most production software is designed by people who have never been on a production floor. You can tell within 60 seconds of opening it. The screens are too busy, the fields are in the wrong order, the buttons are too small for a gloved hand.

We spent 18 months on the floor before writing a single line of production code. It's not glamorous. It's not how SaaS is meant to work. But it's the only way the software ends up worth using.

If you want to walk a floor with us, yours, not a demo one, DM me.`,
  },
  {
    id: 'w3-fri',
    week: 3,
    day: 'Fri · 08:15 Dublin',
    pillar: 'P4',
    title: 'The cost of one missed batch, calculator inside.',
    format: 'Link post + image',
    hashtags: '#FoodManufacturing #IrishDairy #ROI #BatchTraceability #Manufacturing',
    body: `The cost of one missed batch.

Quick exercise. Take your average batch value. Multiply by your monthly error rate. Multiply by 12.

If you don't know your monthly error rate, that's the actual finding.

We built a ROI calculator for Irish and UK artisan producers, runs on your own numbers, gives you a 12-month picture of what spreadsheet errors are quietly costing you.

It's not a sales tool. It tells you the truth. Sometimes the truth is that you're fine. Sometimes it isn't.

Link in the comments. Two minutes to run.

If you'd rather walk through it with me on a call, DM. We can do it in 20 minutes, your real data, not a demo set.`,
  },
  {
    id: 'w4-mon',
    week: 4,
    day: 'Mon · 08:15 Dublin',
    pillar: 'P1',
    title: 'Why per-user pricing kills small dairies.',
    format: 'Text · opinion · 950 chars',
    hashtags: '#FoodManufacturing #IrishBusiness #Pricing #SmallBusiness #SaaS',
    body: `Why per-user pricing kills small dairies.

Most food production software in this market charges €25-€50 per user, per month.

For a small artisan dairy with a QA manager, a dispatch lead, an ops manager and an owner who likes to check the books on Sunday morning, that's €100-€200/month before anyone has produced a single kg of cheese.

So what happens? The owner uses the QA manager's login. The dispatch lead writes things down on paper and the QA manager types them in later. Two of the four "users" of the system are not actually in the system.

You've now paid for software your team doesn't use, while the records you actually rely on live on paper and in someone's memory.

We priced Keystone flat. €790/month, single site, every module, every user within reasonable use (5-15 named accounts). When you hire a third dispatch person, your software bill doesn't go up.

If you're being quoted a per-user food ERP today and your gut says it doesn't sit right, your gut is correct. Reply, and I'll send you a one-page comparison.`,
  },
  {
    id: 'w4-wed',
    week: 4,
    day: 'Wed · 08:15 Dublin',
    pillar: 'P2',
    title: 'Three Irish producers. Three different Excel disasters. Same week.',
    format: 'Carousel · 4 slides (anonymised)',
    hashtags: '#IrishFood #FoodManufacturing #BRCGS #FoodTraceability',
    body: `Three Irish producers. Three different Excel disasters. All in the same week.

Names changed. Details composite. Locations within 90 minutes of each other.

Producer A, a Wexford bakery
Master spreadsheet got too big. Excel crashed during a Lidl mock recall drill. Three hours of work, gone. The retailer was on the phone live. The QA manager had to ring back the next day.

Producer B, a Cork creamery
Dispatch note workbook formula referenced the wrong customer column for three weeks. 11 pallets shipped with the wrong addressee on the paperwork. Tesco Distribution flagged it.

Producer C, a Wicklow charcuterie
The owner's laptop was at home being used by his daughter for college work. The master workbook lives on that laptop. A FSAI inspector arrived unannounced. The owner drove home to get the laptop. The inspector waited 90 minutes in the carpark.

The expensive thing about these stories is not the immediate cost. It's the conversation with the buyer at the next quarterly review.

If you've had one of these weeks, or know it's a matter of time before you do, drop a "✋" in the comments. You're not alone, and the fix is not as hard as you think.`,
  },
  {
    id: 'w4-fri',
    week: 4,
    day: 'Fri · 08:15 Dublin',
    pillar: 'P5',
    title: "Asif's 18 months on the Cashel Blue floor, 7 things he didn't expect.",
    format: 'Text · long-form founder voice',
    hashtags: '#FoodManufacturing #IrishDairy #FarmhouseCheese #StartupLife #FounderJourney',
    body: `Seven things I didn't expect to learn in 18 months on a Tipperary cheese-making floor.

1. The smell. Day one of every visit, I couldn't think about anything else. Day three of every visit, I couldn't smell it. The team have lived in it for 40 years.

2. The dispatch dock is the most stressful 45 minutes of the week. Not the make-room. Not the cellar. The dispatch dock at 3pm on a Friday when one customer's order is 2kg short and the lorry is reversing.

3. Marzena, the QA manager, has more institutional knowledge than the company's CRM. Most of it isn't written down anywhere. She's also one human being who occasionally needs a holiday.

4. The cold-room is colder than you think. The laptop battery dies in 11 minutes. This is why everything in the dispatch flow is paper-first. It's not a process choice. It's a temperature choice.

5. Variable-weight wheels are a software design problem disguised as a production problem.

6. The phrase "we've always done it that way" usually has a very good operational reason behind it. If you don't ask the second question, you'll build software that fights the operation.

7. The owners don't want disruption. They want continuity, made easier. The fastest way to lose them is to use the word "transformation."

If you've walked a food production floor recently and noticed something you didn't expect, share it in the comments. I'd love to read.`,
  },
  // ─── Month 2 ──────────────────────────────────────────────────────
  {
    id: 'w5-mon',
    week: 5,
    day: 'Mon · 08:15 Dublin',
    pillar: 'P1',
    title: "If you can't trace it back to the milk intake, you can't prove anything.",
    format: 'Text · 750 chars',
    hashtags: '#IrishDairy #FoodTraceability #FSAI #BRCGS #FoodSafety',
    body: `If you can't trace a cheese batch back to the morning's milk intake, you can't prove anything else.

You can show me the make-day record. The cellar log. The dispatch note. The customer.

It doesn't matter.

If the link between the silo and the milk intake lives in a separate workbook, owned by a different person, signed off on a different day, with no shared reference number, your chain has a break in it.

Most Irish dairies I've audited have exactly that break.

It's not anyone's fault. The intake person comes in at 6am. The make team comes in at 7. The two systems were built in different decades. The handover is always verbal.

The retailer auditor knows this. So does FSAI. They're looking for the gap.

Software's only real job here is to put one timestamped reference number on the entire chain, end to end, with no manual re-typing.

If you'd like to see how that looks in your own operation, DM. 20-minute walk-through, your data.`,
  },
  {
    id: 'w5-wed',
    week: 5,
    day: 'Wed · 08:15 Dublin',
    pillar: 'P3',
    title: 'EU food safety regulations in plain English.',
    format: 'Carousel · 7 slides',
    hashtags: '#FoodLaw #EU178 #FoodTraceability #BRCGS #FSAI',
    body: `EU food safety regulations in plain English, what Article 18 actually requires from an Irish or UK food business.

Most "explainers" you read on this are written by lawyers for other lawyers. So here it is, in operational language.

What it says (Article 18, paragraphs 1-3):
→ You must be able to identify, for any food product you sell, who supplied you with every raw material that went into it.
→ You must be able to identify, for any food product you sell, who you sold it to (one step forward).
→ Records must be made available to authorities on demand.
→ Records must be sufficient to allow this to happen.

What it does NOT say:
→ It does not specify "software" or "paper."
→ It does not specify a maximum response time.
→ It does not specify what "sufficient" means.

What the case law has filled in:
→ "Sufficient" = enough to perform a full mass-balance reconciliation, including upstream and downstream.
→ "On demand" = same business day for routine, within hours for a live food safety incident.

What BRCGS has added on top:
→ Annual mock recall, both directions, results documented and reviewed.
→ Auditor chooses the starting lot.

The whole regulation fits on one A4 page. The interpretation runs to thousands. Save this for your next compliance prep.

Comments: what's the part you find most ambiguous in practice?`,
  },
  {
    id: 'w5-fri',
    week: 5,
    day: 'Fri · 08:15 Dublin',
    pillar: 'P2',
    title: 'How a Tipperary creamery cut dispatch errors to zero in 90 days.',
    format: 'Text + chart',
    hashtags: '#IrishDairy #SupplyChain #FoodManufacturing #Tesco #Aldi',
    body: `Zero dispatch errors across 90 consecutive days.

This is a real number from Cashel Farmhouse Cheesemakers, measured between February 1 and May 1, 2026. Before Keystone, the baseline was roughly 3-4 dispatch corrections per month, invoice queries, wrong-customer pallet labels, mis-weighed cases.

How did we get from 3-4 to 0?

We didn't optimise the dispatch team. The dispatch team was already excellent. We removed three points where the operation was being asked to do the impossible:

1. Customer-mapped SKUs. The same internal cheese SKU, mapped once, generates a Tesco Distribution dispatch note, an Aldi Stores dispatch note, and a SuperValu dispatch note, each in the format the buyer expects. No re-keying. No copy-paste from a workbook into a Word template.

2. Variable-weight reconciliation. Every wheel weighed at the dispatch table is reconciled live against the batch master. Mis-weighed cases are caught at the bay, not at the customer's goods-in.

3. Pallet label printing inside the dispatch flow. The label prints when the dispatch note saves. They cannot exist independently. The mismatch class of error is mathematically impossible.

Three small choices. Zero errors for 90 days.

If you ship into Irish or UK retail and dispatch errors are eating your Monday mornings, DM. Worst case: 20 minutes, my time, your numbers.`,
  },
  {
    id: 'w6-mon',
    week: 6,
    day: 'Mon · 08:15 Dublin',
    pillar: 'P1',
    title: 'Your QA manager is a single point of failure. Software is the redundancy.',
    format: 'Text · opinion',
    hashtags: '#FoodManufacturing #QualityAssurance #BusinessContinuity #SmallBusiness',
    body: `Your QA manager is a single point of failure.

She knows the workbook. She knows the customer quirks. She knows which lot you used in the August make for the December rinds. She knows which fields in the FSAI form actually matter and which ones are filler.

She also has a personal life. She gets sick. She goes on holiday. She might go on maternity leave next year.

When she's not in, your operation runs on hope.

This is not a critique of QA managers. The opposite. Excellent QA managers in artisan food are rare and underpaid. What I'm saying is: she shouldn't be your business continuity plan.

Software is not a replacement for her. Nothing is.

But software is a transcript of what she does, so that on the Thursday in November when she's home with a sick child, her cover can open one screen and follow the chain without ringing her.

If you would not survive two weeks without your QA manager, you don't have a software problem. You have a risk register problem. Software is part of the answer.

What's your continuity plan look like, honestly?`,
  },
  {
    id: 'w6-wed',
    week: 6,
    day: 'Wed · 08:15 Dublin',
    pillar: 'P3',
    title: 'The 2 spreadsheets every Irish dairy has, and how each one will eventually fail.',
    format: 'Image post · diagram',
    hashtags: '#IrishDairy #FoodManufacturing #Spreadsheets #BRCGS #FoodSafety',
    body: `Every Irish dairy I've walked into in the last 24 months has exactly two master spreadsheets.

Spreadsheet 1, The Master Batch Workbook
Lives in the QA office. Tabs for intake, silo, make-day, batch, COA, mock recall log. Updated daily. Backed up "sometimes." Owned by the QA manager.

Failure mode: it gets too big. Excel chokes around 40k rows. Filters get slow. The Friday afternoon update takes 90 minutes instead of 20. Eventually a row gets deleted by mistake and nobody notices for three weeks. The auditor finds the gap.

Spreadsheet 2, The Dispatch Run Workbook
Lives on the dispatch lead's laptop. Tabs by customer. Pricing, customer SKUs, last week's run, this week's run. Updated weekly. Backed up by emailing it to the owner.

Failure mode: formulas. The dispatch workbook is held together with VLOOKUPs and one IFERROR statement nobody remembers writing. One day a column gets inserted in the wrong place and three weeks of dispatches reference the wrong customer.

Both of these workbooks are doing the work of software. They just cost less up front.

The expensive bit is what they cost when they fail, and they always eventually fail.

Save this if you recognised one of them. Tag the QA manager if you recognised both.`,
  },
  {
    id: 'w6-fri',
    week: 6,
    day: 'Fri · 08:15 Dublin',
    pillar: 'P6',
    title: '60-sec demo: Tesco-spec dispatch note printed in 4 seconds.',
    format: 'Video · 60 sec',
    hashtags: '#FoodSupplyChain #FoodManufacturing #Tesco #Dispatch #IrishDairy',
    body: `60-second demo. Real Tesco-spec dispatch note. From "click save" to "off the printer" in four seconds.

What you'll see:
→ The dispatch screen. Real batches, real weights.
→ The "save dispatch" click.
→ Two PDFs generated server-side: the dispatch note in Tesco Distribution format, and the matching pallet label.
→ Both print automatically. The pallet label to the warehouse Zebra; the dispatch note to the office laser.
→ The data lands in the customer's downstream system formatted correctly the first time. No correction email on Monday.

It is genuinely four seconds. There is no theatre.

The boring bit is what isn't on screen: 18 months of mapping every retailer's specific quirks into one template engine, so that the dispatch team never has to think about it again.

If you ship into Tesco, Aldi, Lidl, SuperValu, Dunnes or Musgraves and you're still hand-formatting dispatch notes, reply or DM. 20 minutes, your real customer specs, no obligation.`,
  },
  {
    id: 'w7-mon',
    week: 7,
    day: 'Mon · 08:15 Dublin',
    pillar: 'P3',
    title: 'What "audit-ready" actually means in 2026.',
    format: 'Text · long-form · 1200 chars',
    hashtags: '#BRCGS #FSAI #AuditReady #FoodSafety #FoodManufacturing',
    body: `"Audit-ready" is one of the most over-used phrases in food manufacturing software.

Most vendors use it to mean "we have lots of records." That is not what an auditor means.

Here are five concrete tests. If your operation passes 4 of 5, you are audit-ready. If you pass 3 of 5, you are hoping. If you pass 2 of 5, you are going to fail your next surveillance audit.

Test 1, Backward trace, randomly chosen lot
The auditor picks any raw material lot from the last 12 months. You produce a full mass-balance reconciliation, with timestamps, within 4 hours.

Test 2, Forward trace, randomly chosen batch
The auditor picks any output batch you've dispatched. You produce a complete downstream list, all customers, all dispatch dates, all kg shipped, within the same window.

Test 3, Mock recall on a 2-year-old supplier lot
Not a recent one. A salt lot from 2023. Can you still reach every downstream batch? Can you tell me whether any of those batches are still in shelf life with a customer?

Test 4, Audit trail integrity
Pick any record. Tell me who created it, who amended it, when, and what the original value was. No software, this is impossible. With software, this should be one click.

Test 5, QA manager continuity
Your QA manager is unavailable. Her cover can open one screen and walk through the entire chain for any product without ringing her.

Comments: which of the five do you struggle with most?`,
  },
  {
    id: 'w7-wed',
    week: 7,
    day: 'Wed · 08:15 Dublin',
    pillar: 'P4',
    title: 'Free template: BRCGS §3.9 mock recall test record (PDF + editable Word).',
    format: 'PDF + DOCX · link in comments',
    hashtags: '#BRCGS #BRCGSIssue9 #MockRecall #FoodSafety #AuditReady',
    body: `Free template.

A BRCGS §3.9 mock recall test record, in the format auditors actually accept. Pre-filled with example data so you can see what a "good" answer looks like.

What's in it:
→ Starting lot identification (with the bit auditors look for first)
→ Upstream chain reconciliation table
→ Downstream chain reconciliation table
→ Mass-balance summary
→ Sign-off page with timestamps and responsibilities
→ A short reviewer's note section (the bit most people leave blank)

It's a PDF and an editable Word doc. No signup. No email gate. Link in the comments.

Built it because the BRCGS guidance is generic and most QA managers I work with are reinventing the same record from scratch every year. Take this, use it, change the logo, claim it as your own.

If a fellow QA manager you know will benefit, tag them. They'll thank you in October.`,
  },
  {
    id: 'w7-fri',
    week: 7,
    day: 'Fri · 08:15 Dublin',
    pillar: 'P5',
    title: 'November 2024 to March 2025, what changed in 4 months on the floor.',
    format: 'Text · long-form founder voice',
    hashtags: '#FounderJourney #ProductDevelopment #IrishStartup #FoodManufacturing',
    body: `I walked the Cashel Farmhouse Cheesemakers floor for the first time on the 11th of November 2024. I shipped the first working version of Keystone's production module on the 18th of March 2025. Four months in between.

Here's what changed in those four months that I didn't expect.

Week 1, I thought I'd build them an inventory module. They didn't need one. They needed a customer-mapping module. The whole product re-pivoted in week one.

Week 3, I sat at the dispatch table on a Friday afternoon for three hours and didn't open my laptop once. Just watched. The Friday afternoon dispatch flow has its own logic and rhythm. It is not what I would have written software for if I'd designed in an office.

Week 6, Marzena showed me Salt Lot 2200. A single 25kg bag of salt that was used across 14 cheese batches over a 5-day window. The salt-lot example is now the canonical mock recall test in Keystone's demo dataset.

Week 9, I tried to convince Sergio to give up paper for the dispatch run sheet. He let me try for a day. By the end of the day, I had agreed to print every dispatch note on paper as well as save it digitally. The cold room reality won. I was wrong.

Week 14, First production go-live. One screen, one customer, one batch. It went through clean. Marzena rang me three times that morning to point out where it was clunky. Every one of those notes is now a Keystone feature.

The lesson, if you're building a product for a real operation: spend more time watching than speaking. The product almost designs itself if you do.`,
  },
  {
    id: 'w8-mon',
    week: 8,
    day: 'Mon · 08:15 Dublin',
    pillar: 'P1',
    title: 'Excel users vs Keystone users, recall drill, head to head.',
    format: 'Image · 1 frame infographic',
    hashtags: '#FoodTraceability #BRCGS #Recall #IrishDairy #FoodSafety',
    body: `Head to head. Same recall scenario. Same starting lot.

Recall scenario: Salt Lot 2200, used across 14 batches between January 14-19.

Find:
→ Every batch that contains this lot.
→ Every dispatch that contains those batches.
→ Every customer affected, by kg.

Excel user: 4 hours, 38 minutes. Three formulas re-written mid-drill. One coffee. One delivery person interrupted three times. A nagging feeling that one batch might have been missed.

Keystone user: 4 minutes, 12 seconds. From clicking the lot to PDF response pack ready for the buyer.

Difference: 4 hours, 34 minutes.

If your retailer rings on a Tuesday and asks you to do this, and they will, this year, those 4 hours and 34 minutes happen on the same call, while they wait. Or while they don't wait.

Run the same exercise tomorrow morning against your own data and see where you land. The number itself is the diagnosis.`,
  },
  {
    id: 'w8-wed',
    week: 8,
    day: 'Wed · 08:15 Dublin',
    pillar: 'P2',
    title: 'Every feature in Keystone solved an actual problem in a real Irish dairy.',
    format: 'Text post · pin to top of profile',
    hashtags: '#IrishDairy #FarmhouseCheese #Tipperary #FoodManufacturing #BuiltInIreland',
    body: `Keystone wasn&rsquo;t designed in a boardroom. It was built alongside Cashel Farmhouse Cheesemakers in Fethard, Co. Tipperary.

Every screen was designed at a real production table.
Every workflow was tested on their floor before shipping.
Every "feature" you see in Keystone solved an actual problem in a real Irish dairy, not a hypothetical one.

Long-form on-record testimonials from the Cashel Blue team are being collected for publication in the coming weeks. In the meantime, if you&rsquo;d like to walk the same floor, the same operation, and see Keystone working in its native environment, we can arrange a reference call. Reply or DM.

Used with the written permission of J&amp;L Grubb Ltd. Cashel Blue® and Crozier Blue® are registered trademarks of J&amp;L Grubb Ltd.`,
  },
  {
    id: 'w8-fri',
    week: 8,
    day: 'Fri · 08:15 Dublin',
    pillar: 'P6',
    title: 'Pricing teardown, MRPeasy, Wherefour, SoftTrace, Keystone.',
    format: 'Carousel · 4 slides',
    hashtags: '#FoodManufacturingSoftware #Pricing #FoodERP #SaaSPricing #IrishBusiness',
    body: `Honest pricing comparison. No mud-slinging. Where each of the four wins.

MRPeasy, circa €49/user/month + €49/manufacturing-user/month
Wins on: low entry price for very small teams, broad manufacturing module library.
Loses on: per-user pricing punishes you for hiring; food-specific traceability is bolted on rather than native.

Wherefour, circa US$249/user/month for the food edition
Wins on: deeply food-specific, strong batch-level traceability, decent US food regulator alignment.
Loses on: per-user pricing at this rate gets eye-watering above 4-5 users; weaker on Irish/UK retailer-specific dispatch formats (Tesco, Aldi, Lidl).

SoftTrace, bespoke pricing, typically €18-€30k year one for a small dairy
Wins on: most established product in Irish dairy specifically; very strong FSAI and BRCGS lineage; integrates with milk procurement systems.
Loses on: cost; implementation time; harder to roll back if it doesn't fit.

Keystone, €790/month single-site, €1,290 multi-site, every module, every reasonable user
Wins on: flat pricing, every module included, Irish dairy-first design.
Loses on: younger product, smaller installed base, no DACH or Nordic localisation yet.

The right answer depends on your scale and your speed.

If you're a small-to-medium Irish or UK artisan producer with 15 or fewer users and you ship into Irish/UK retail, Keystone is built for you specifically. If you have 60 users and a multi-country footprint, talk to SoftTrace.

If you're being quoted by any of the above, DM me and I'll give you a frank opinion on whether it's the right fit. Not every conversation needs to end at Keystone.`,
  },
  // ─── Month 3 ──────────────────────────────────────────────────────
  {
    id: 'w9-mon',
    week: 9,
    day: 'Mon · 08:15 Dublin',
    pillar: 'P1',
    title: "If your software can't print a Tesco-spec pallet label, it isn't traceability.",
    format: 'Text + image · 850 chars',
    hashtags: '#Tesco #Aldi #Lidl #IrishRetail #FoodManufacturing #PalletLabels',
    body: `If your traceability software can't print a Tesco-spec pallet label, it isn't traceability software.

It's a database with extra steps.

Tesco Distribution wants the pallet label on the short side. Aldi UK wants it on the short side too but with a different barcode density. Lidl Ireland wants it on the long side, in a slightly different format from Lidl GB.

A traceability system that gives you the data but not the label is solving a problem half-way. You spend twice, once for the software, once for the dispatch team to manually re-key the data into a separate label-printing tool.

Three things your dispatch flow needs to do, end-to-end, in one click:

1. Generate the customer's dispatch note in their specific template (yes, the one their goods-in person actually reads).
2. Print the matching pallet label in the customer's specific format.
3. Reconcile both back to the variable-weight batch master.

If one of those three lives in a different system, you have integration risk every Friday at 3pm.

This is the actual measure of food traceability software for an Irish or UK manufacturer. Save this when you next see a "traceability" demo.`,
  },
  {
    id: 'w9-wed',
    week: 9,
    day: 'Wed · 08:15 Dublin',
    pillar: 'P3',
    title: 'FSAI mock recall, what they actually test, slide by slide.',
    format: 'Carousel · 8 slides',
    hashtags: '#FSAI #Recall #IrishFood #FoodSafety #BRCGS',
    body: `I've reviewed 14 anonymised FSAI mock recall exercises from Irish food producers over the last 18 months. The pattern in what they test is remarkably consistent. Here it is.

What FSAI actually tests in an Irish mock recall:

Slide 1, Pick a lot. Their pick, not yours. Usually 6-18 months old. Rarely a fresh lot.

Slide 2, Ask for the upstream chain in 4 hours. Mass-balance reconciliation. Every supplier lot accounted for.

Slide 3, Ask for the downstream footprint in the same window. Every customer, every kg, every dispatch date. They want a single document.

Slide 4, Ask who would receive the customer notification. They want named people, not "the office."

Slide 5, Ask for the most recent customer notification you've drafted (whether sent or not). They want to see the template, not just hear about it.

Slide 6, Ask to see the audit trail. Who created the records. Who amended them. They want timestamps. Sticky-notes do not count.

Slide 7, Ask whether you've done a mock recall on this exact starting lot before. They are looking for whether you rehearsed.

Slide 8, Ask what happens if your QA manager is on holiday. They want a documented cover plan, not "we'd ring her."

If you can answer all 8 with no preparation time, you are ready. If 1-3 of them give you pause, you should run a mock yourself before they do.

DM "MOCK" for the full anonymised pattern as a PDF.`,
  },
  {
    id: 'w9-fri',
    week: 9,
    day: 'Fri · 08:15 Dublin',
    pillar: 'P6',
    title: 'Live tour: dispatch + traceability + COA on one screen.',
    format: 'Video · 90 sec narrated',
    hashtags: '#FoodManufacturing #Traceability #Dispatch #COA #IrishDairy',
    body: `90-second narrated tour. Three modules. One screen. Real customer data (anonymised).

What you'll see in 90 seconds:

→ The dispatch screen, mid-Friday afternoon. Active customer orders queued up by collection time.
→ One click into a dispatch. Behind the scenes: pallet label generates, dispatch note generates, COA pulls from the make-day record, all three reconcile to the variable-weight batch master.
→ One click on the COA. Full chemistry, microbiological, moisture/fat ratios. Lab data tied directly to the batch.
→ One click on the batch ID. Full upstream chain, silo, milk intake, supplier lot, and the full downstream, every customer this batch has reached.

The "single screen" promise made concrete. Built around the way a real Irish dairy dispatch team actually works on a Friday afternoon at 2:45pm.

If you'd like a tailored walk-through using your own customer specs (Tesco / Aldi / Lidl / SuperValu / Dunnes / Musgraves), reply or DM. 20 minutes, founder-led, no slides.`,
  },
  {
    id: 'w10-mon',
    week: 10,
    day: 'Mon · 08:15 Dublin',
    pillar: 'P5',
    title: 'Why "designed-to-support" is the only honest compliance claim software can make.',
    format: 'Text · long-form opinion',
    hashtags: '#BRCGS #FSAI #FoodCompliance #FoodSafety #IrishFood',
    body: `Software cannot be FSAI-certified. Software cannot be BRCGS-certified. Software cannot be ISO 27001-certified for food traceability outcomes.

Your operation can.

The software supports your operation in being certifiable. Anyone selling you otherwise is selling theatre.

This matters because the food manufacturing software market is full of phrases like:

→ "BRCGS-certified platform"
→ "FSAI-approved software"
→ "ISO-compliant traceability"

These phrases are technically meaningless. There is no BRCGS certificate for software. There is no FSAI approval scheme for software vendors.

What you can honestly say:
→ "We are designed against the BRCGS traceability requirements."
→ "We support the FSAI Article 18 requirements for the businesses that use us."
→ "We are aligned to ISO 27001 controls; our hosting provider holds the certificate."

Each of those is a specific, defensible claim that an auditor will accept and that doesn't expose you (the user) when they ring your vendor for evidence.

We deliberately write Keystone's claims this way. It's less marketable. It's more correct.

If you've been pitched the certified version of these phrases recently, by any vendor, not just ours, ask them for the actual certificate. The answer is the diagnostic.`,
  },
  {
    id: 'w10-wed',
    week: 10,
    day: 'Wed · 08:15 Dublin',
    pillar: 'P2',
    title: 'Tipperary, Wicklow, West Cork, three Irish brands, one platform.',
    format: 'Image · logo strip',
    hashtags: '#IrishFood #IrishDairy #FoodManufacturing #FarmhouseCheese',
    body: `Three Irish food producers. Three different products. Three different sets of retailer relationships. One platform under all of them.

→ Cashel Farmhouse Cheesemakers (Tipperary), farmhouse blue cheese, international export footprint, Tesco / Aldi / SuperValu / multiple wholesale.
→ [Producer B, permission pending] (Wicklow), pending permission to name.
→ [Producer C, permission pending] (West Cork), pending permission to name.

What surprised us in the implementations:

The operational similarities are deeper than we expected. Variable-weight wheel reconciliation. Friday afternoon dispatch crunch. Multi-customer SKU mapping. Mock recall test fatigue. All three have the same fundamental shape.

The differences are also deeper than we expected. One ages in the cellar for 12 weeks; another moves stock weekly. One has 5 retail customers; another has 40+ between retail and food-service. One has been in Tesco UK for a decade; another only ships within 50 miles.

What this tells us about food software: 80% can be standardised. 20% has to be set up per producer. We design around that ratio, not against it.

If you'd like to become the next Irish producer on the platform, reply or DM. 20 minutes, no slides.`,
  },
  {
    id: 'w10-fri',
    week: 10,
    day: 'Fri · 08:15 Dublin',
    pillar: 'P4',
    title: 'Free download, 12-month dairy production planning template (A4 wall format).',
    format: 'PDF · A4 wall planner · link in comments',
    hashtags: '#IrishDairy #ProductionPlanning #FarmhouseCheese #FoodManufacturing',
    body: `Made you another thing.

A 12-month production planning template for Irish and UK artisan dairies. Designed to be printed at A4 and pinned on the wall behind the QA manager's desk.

What's on it:
✓ Production cycle calendar, mapped to typical Irish/UK retail buying windows.
✓ BRCGS audit slot indicator (with the 3-month "prep" window highlighted).
✓ FSAI inspection window (the ones that arrive announced, and the ones that don't).
✓ Bank holiday cover indicator (the bit that always catches you out in August).
✓ Quarterly mock recall schedule.
✓ Maternity / paternity / annual leave overlay.

Print it. Pin it. Update once a quarter.

Free. No signup. No email gate. Link in the comments.

Built it because we genuinely couldn't find a good one for the Irish/UK market. If you find it useful, share it forward to a fellow producer. If you find it lacking, tell me what's missing and I'll add it for the next version.`,
  },
  {
    id: 'w11-mon',
    week: 11,
    day: 'Mon · 08:15 Dublin',
    pillar: 'P1',
    title: 'What happens to your dairy when your QA manager goes on maternity leave?',
    format: 'Text · long-form · 1300 chars',
    hashtags: '#QualityAssurance #FoodManufacturing #BusinessContinuity #IrishDairy',
    body: `What happens to your dairy when your QA manager goes on maternity leave?

This is not a hypothetical. It happened to a producer I know in the south-west, in autumn 2024. She gave eight weeks' notice. They thought they were ready.

Three weeks in, here's what they discovered:

→ The master batch workbook has 23 tabs. The cover person can open 8 of them. The other 15 use named ranges that only the QA manager remembers.

→ The mock recall procedure is in a Word document last updated in 2022. Three of the named contacts on it have left the company.

→ The Tesco buyer rings on a Wednesday and asks for traceability evidence on a 9-month-old batch. The cover person spends 6 hours on it and gets within 80% of the answer. The QA manager later, from maternity leave, on her own phone, finishes the last 20% in 12 minutes.

→ The FSAI inspector arrives unannounced. The cover person handles it. He fails one record because nobody on-site can explain why a row was crossed out in pencil.

The company survived. The owner now keeps me informed of every QA hire and every maternity announcement in their orbit, because she does not want to live through it twice.

Three things to do this week, even if you have no maternity announcements pending:

1. Make a list of every operational task your QA manager does that lives nowhere else.
2. For each one, write down what would happen if she were unavailable for 12 weeks.
3. Show the list to her. She'll add three more things you missed.

This is the actual business case for software. Not "more records." Continuity.`,
  },
  {
    id: 'w11-wed',
    week: 11,
    day: 'Wed · 08:15 Dublin',
    pillar: 'P3',
    title: 'BRCGS vs Issue 10, what is already in the consultation draft.',
    format: 'Carousel · 5 slides',
    hashtags: '#BRCGS #BRCGSIssue10 #FoodSafety #FoodManufacturing #AuditReady',
    body: `BRCGS Issue 10 consultation draft, three changes Irish and UK producers should start preparing for, even if you've only just adopted BRCGS traceability requirements.

Important caveat: Issue 10 is in consultation. Final language can shift. What's below is from the publicly-circulated draft, not the final standard. Treat it as direction, not gospel.

Change 1, Continuous traceability vs annual mock recall
BRCGS traceability requirements says one mock recall per year, both directions. The Issue 10 draft signals a shift toward "continuous traceability competence", meaning the auditor can ask for an unrehearsed traceability test at any surveillance visit, not just at the annual one. If your records can't sustain that, you'll know quickly.

Change 2, Stronger expectations around digital integrity
BRCGS traceability requirements is system-agnostic, paper, Excel, software, all in scope. The Issue 10 draft language leans more toward "demonstrable record integrity" which most easily means audit trails. Spreadsheets do not have audit trails. This is the most operationally significant change.

Change 3, Closer alignment with FSMA-style supplier risk requirements
The draft pulls in language closer to US FDA's FSMA Final Rule on supplier risk verification. For Irish/UK producers exporting to the US, this is welcome, it reduces duplicate work. For producers selling only to the UK, it adds a small amount of upstream supplier-screening work.

What I'd do, if my operation runs BRCGS-certified:
→ Run a mock recall on a randomly-chosen lot this quarter. Not next quarter.
→ Audit your record integrity. Identify which records currently have no audit trail.
→ Map your top 5 supplier risks. Document the verification you do today.

None of this is urgent. All of it is coming.

Save this for your next BRCGS prep meeting.`,
  },
  {
    id: 'w11-fri',
    week: 11,
    day: 'Fri · 08:15 Dublin',
    pillar: 'P6',
    title: '60-sec demo, customer-mapped SKUs in action.',
    format: 'Video · 60 sec',
    hashtags: '#FoodManufacturing #IrishRetail #SKU #SupplyChain #Dispatch',
    body: `60 seconds. One internal cheese SKU. Five different retailer presentations. One click each.

Same wheel. Same batch. Same chemistry. Five entirely different dispatch lives:

→ Tesco Distribution Tipperary, short-side pallet label, 8-digit Tesco internal code, dispatch note in Tesco-Distribution.com format.
→ Aldi UK, long-side pallet label, Aldi internal SKU, dispatch note via Aldi's NDC portal format.
→ Lidl Ireland, short-side label, EAN-13 barcode density, dispatch note in Lidl GS1-128 format.
→ SuperValu Musgrave, small-side label, Musgraves internal code, dispatch note as PDF email attachment.
→ Wholesale customer, generic label, customer-defined SKU, dispatch note as plain PDF.

What used to take 5 separate Word templates and 5 different barcode generators is now 5 buttons. Each button maps the one internal SKU to the customer's specific output.

Build the master once. Sell to anyone, in any format, for the rest of the product's life.

If you ship into more than 2 of the above and your dispatch team currently re-keys customer-specific data into Word templates, DM me. 20 minutes, your specs, no slides.`,
  },
  {
    id: 'w12-mon',
    week: 12,
    day: 'Mon · 08:15 Dublin',
    pillar: 'P1',
    title: '5 Irish dairies that will not be Excel-based by Christmas.',
    format: 'Text · 4 lines · scarcity play',
    hashtags: '#IrishDairy #FoodManufacturing #Tipperary #IrishBusiness',
    body: `Five Irish dairies that will not be Excel-based by Christmas.

(Not naming them. You'll see when they post about it.)

If you want to be the sixth, reply.

10 implementations remaining at the 2026 introductory pricing. After that the implementation fee goes up. The licence price doesn't.`,
  },
  {
    id: 'w12-wed',
    week: 12,
    day: 'Wed · 08:15 Dublin',
    pillar: 'P5',
    title: 'How we wrote the privacy policy without a DPO.',
    format: 'Text · long-form founder/education',
    hashtags: '#GDPR #DataProtection #IrishBusiness #StartupHonesty #PrivacyByDesign',
    body: `How we wrote the privacy policy without a DPO.

(And why that's actually more GDPR-honest than most "DPO assigned" boilerplate.)

When CloudVoro was at four people, we did the same thing most early-stage Irish SaaS does: we Googled "GDPR DPO" and wrote "Data Protection Officer assigned" into our privacy policy. Because the templates told us to.

Then I actually read GDPR Article 37.

Article 37 says: a DPO is mandatory if you are (a) a public authority, (b) processing personal data on a large scale as your core activity, or (c) doing large-scale processing of special-category data. None of those describe us, today.

What's actually mandatory under GDPR for a company our size:
→ Identify someone responsible for data protection (we did, me).
→ Document data flows and lawful bases.
→ Have a records-of-processing register.
→ Respond to data subject requests within 30 days.

That person does not have to be called a "DPO" and, in fact, GDPR specifies that if you do call someone a DPO, they have certain independence requirements that small companies typically can't honour. So calling them a DPO when you can't operationally back it up is the opposite of compliant.

We rewrote the policy. The named role on it is "Data Protection Lead." The same person, doing the same actual work, with a more honest title.

The bigger principle here: most GDPR theatre is performed by companies that can't actually deliver the underlying requirement, because they think the language is the compliance. The language isn't the compliance. The records are.

Read our policy in the comments. It's two pages. No legalese.`,
  },
  {
    id: 'w12-fri',
    week: 12,
    day: 'Fri · 08:15 Dublin',
    pillar: 'P2',
    title: 'Q1 wrap, every post, every learning, every customer signed.',
    format: 'Carousel · 12 slides · retrospective',
    hashtags: '#StartupJourney #IrishStartup #FoodManufacturing #YearInReview',
    body: `Q1 2026 wrap. 12 weeks. 36 LinkedIn posts. Here's what worked, what didn't, and what we changed.

What worked:
→ Specific operational stories outperformed generic "compliance" content by ~3x in engagement.
→ Founder voice (P5) posts on Fridays drove the most DMs.
→ Free downloads (P4), the FSAI audit checklist, the BRCGS mock recall template, the production planning calendar, drove the highest demo conversion. Worth the time to make.

What didn't:
→ Carousel posts under 4 slides underperformed. People wanted depth or punch, not middle.
→ Posts mentioning "AI" got fewer comments than posts not mentioning it. Speaks to our audience.
→ Tuesday posts underperformed Mondays by ~40%. Mondays own the food sector LinkedIn feed.

The numbers (12 weeks):
→ New followers: [X]
→ Demos booked from inbound: [X]
→ Reference calls introduced: [X]
→ Customers signed: [X]

What's changing in Q2:
→ More Cashel Blue floor video, less typography slides.
→ Adding Instagram fully, not as an afterthought.
→ Doubling the "Compliance Decoded" frequency in the run-up to Q3 audit season.

Thank you to every QA manager, owner, ops director and dispatch lead who reacted, commented, DM'd, or quietly forwarded a post to a colleague. We see you.

Q2 starts Monday. Same time. Different posts.`,
  },
];
